import { isoZ, z } from 'my-zod'
import { enumOptionsToObject } from '../../internal'

export type DayOfWeek = 'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat'

export const priceUnitOptions = ['fixed', 'percentIncrease', 'percentDecrease', 'fixedIncrease', 'fixedDecrease'] as const
export const priceUnits = enumOptionsToObject(priceUnitOptions)
export type PriceUnit = (typeof priceUnitOptions)[number]

export const polarisRentalProductPricing = z.object({
  id: z.string(),
  productId: z.string(),
  /**  */
  priority: z
    .number()
    .describe(
      'Often, two prices will apply to a particular reservation, in these cases, the price with the highest priority will win.'
    ),
  startDate: isoZ.monthDay().nullable().describe('This price will only apply if the reservation is after this monthDay'),
  endDate: isoZ.monthDay().nullable().describe('This price will only apply if the reservation is before this monthDay'),
  daysOfWeek: z
    .enum(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'])
    .array()
    .nullable()
    .initFn(() => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'])
    .describe('This price will only apply if the reservation is on one of the days of the week specified'),
  assetFamilyIds: z.string().array().nullable().describe('If specified, this will break the price out by asset family'),

  /** Price cannot vary by time for pickups and delivery since price is by day. */
  startTime: isoZ
    .time()
    .nullable()
    .describe('The price will only apply if the reservation is after this time. Must be on the hour'),
  endTime: isoZ
    .time()
    .nullable()
    .describe('The price will only apply if the reservation is before this time. Must be on the hour '),
  minDuration: z.number().nullable().describe('The price will only apply if the reservation is longer than this duration'),
  maxHoursBeforeReservation: z
    .number()
    .int()
    .nullable()
    .describe('If a customer books within N hours (exclusive) of the reservation start, this price will apply'),
  maxAvailableUnits: z
    .number()
    .int()
    .nullable()
    .describe('If there are N units available (exclusive) at the time the customer books, this price will apply'),
  unit: z.enum(priceUnitOptions),
  amount: z.number().int().nonnegative()
})

export type polarisRentalProductPricing = z.infer<typeof polarisRentalProductPricing>
